// 关于我们页「副本」数据:从原 about-us.data.ts 派生(品牌改名),并新增 3 个 banner 文案。
// 原数据文件不改 → 原 /auth/about 页不受影响。
import { ABOUT as ABOUT_ORIG, IMG_BASE, type Lang, type Pair } from './about-us.data';

export { IMG_BASE };
export type { Lang, Pair };

// 副本切图素材目录(crop-about-v2.py 产出)
export const IMG_V2 = '/images/about/v2';

// 品牌改名:繁體→易學平台,英文→EASE。
// 邮箱/网址(小写、无 ®)不被命中,保持原样。
const renameT = (s: string) =>
  s
    // 「視易學 <b>V2elearn®</b>」→「<b>易學平台</b>」:品牌词被 <b> 标签隔断,需先单独合并(否则残留「視易學」)
    .replace(/視易學\s*<b>\s*V2elearn®?\s*<\/b>/g, '<b>易學平台</b>')
    .replace(/視易學\s*V2elearn®?/g, '易學平台')
    .replace(/V2elearn®/g, '易學平台')
    // 「視易學V2elearn®平台」→「易學平台」已含「平台」,尾随的「平台」会重复,合并去重
    .replace(/易學平台\s*平台/g, '易學平台');
const renameE = (s: string) => s.replace(/V2elearn®/g, 'EASE');

const isPair = (n: unknown): n is Pair =>
  !!n && typeof n === 'object' && !Array.isArray(n) &&
  Object.keys(n as object).length === 2 &&
  typeof (n as Pair).t === 'string' && typeof (n as Pair).e === 'string';

// 递归深拷贝 + 对每个 {t,e} 套用改名;非 Pair 字符串(如论文标题)原样保留。
function brandRename<T>(node: T): T {
  if (Array.isArray(node)) return node.map(brandRename) as unknown as T;
  if (isPair(node)) return { t: renameT(node.t), e: renameE(node.e) } as unknown as T;
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) out[k] = brandRename(v);
    return out as T;
  }
  return node;
}

export const ABOUT: typeof ABOUT_ORIG = brandRename(ABOUT_ORIG);

// ---- 3 个 banner 的文案(t=繁體 / e=English,已套用改名)----
// 注:正文/项目符号/特色描述的精确字句在 Task 3/4/5 对照源 PNG 逐字转写后回填。
export const BANNERS = {
  intro: {
    title: { t: '易學平台 是什麼？', e: 'What is EASE?' },
    lead: {
      t: '易學平台 是由香港大學可視化學習實驗室 (Visual Learning Lab) 研發的可視化學習系統，旨在促進教與學，通過使用可視化技術將教學錄像轉化成為高效能、低成本的學習資源。成果在中小學試用和推廣，以提升教學品質和教育公平。',
      e: 'EASE is a visualized learning system developed by Visual Learning Lab, The University of Hong Kong. It aims to improve teaching and learning efficacy, and transforms educational videos into high-efficacy, low cost-learning resources through visualization technologies. It can also be used to improve the quality and equity of school education.',
    },
    systemTitle: { t: '易學平台 系統包括：', e: 'EASE system includes:' },
    student: {
      name: { t: '易學平台 Student', e: 'EASE Student' },
      subtitle: { t: '基於課堂錄像的可視化學習系統', e: 'A visualization-enhanced video-based e-learning platform for students' },
      img: 'intro-student',
      points: [
        { t: '學習資源豐富呈現，激發興趣自由探索', e: 'Explore a bundle of learning resources for a deeper understanding' },
        { t: '交互式可視化體驗，一鍵聯動樂在其中', e: 'Interact with attractive visualizations for insights into classroom dialogues' },
        { t: '學情分析與可視化，學習狀態隨時掌握', e: 'Stay informed about learning performance via visualized reports' },
      ] as Pair[],
    },
    teacher: {
      name: { t: '易學平台 Teacher', e: 'EASE Teacher' },
      subtitle: { t: '檢視教學成效和促進專業發展的平台', e: 'An intelligent platform for improving teaching quality and teacher professional development' },
      img: 'intro-teacher',
      points: [
        { t: '自動轉錄可視化，課堂數據一覽無餘', e: 'View the landscape of classroom dialogues through automatic transcription and visualization' },
        { t: '機器批改與分析，學生功課輕鬆管理', e: 'With auto-evaluation and analysis, homework management is easier than ever' },
        { t: '群組協作與交流，促進反思改善教學', e: 'Collaborate, reflect and practice in professional learning communities' },
      ] as Pair[],
    },
    whyTitle: { t: '為什麼選擇 易學平台？', e: 'Why choose EASE?' },
    features: [
      { img: 'intro-cycle', title: { t: '可視化學習，激發學習興趣', e: 'The thrill of visualization' }, desc: { t: '透過可視化技術，將課堂錄像、課堂對話等學習資源以及學習過程進行多維度可視化呈現，增強學習動力，提升學習效能。', e: 'EASE leverages visualization technologies to transform educational videos into effective learning resources and uses multi-level multi-dimensional visual representations to enhance learning motivation and efficiency.' } },
      { img: 'intro-venn', title: { t: '協作式探究，促進教學發展', e: 'Collaborate, we thrive' }, desc: { t: '支持學生進行自我調整、協作學習及師生協作。教師更可通過觀課進行自我反思、協作式探究，促進專業發展。', e: 'EASE supports students to engage in self-regulated learning and provides opportunities for collaborative learning with peers and teachers. It also facilitates teachers to engage in evidence-based self-reflection and collaborative inquiry through class observation.' } },
      { img: 'intro-security', title: { t: '訂製化體驗，穩健可靠安全', e: 'Your experience, you control' }, desc: { t: '個性化的操作體驗和學習界面，滿足多種教學場景需求。由香港大學資訊科技服務部提供網絡和數據服務，信息安全和私隱有保障。', e: 'At EASE, teachers can customize the experience and user interface to suit the needs of various teaching scenarios. Teachers have control of privacy on materials uploaded to the platform, and the data is secured with support from IT Services, the University of Hong Kong.' } },
      { img: 'intro-multidevice', title: { t: '跨平台適配，隨時隨地學習', e: 'Enjoy learning, anytime anywhere' }, desc: { t: '適配PC和移動端各類設備，滿足教師與學生彈性學習需求。', e: 'EASE can be accessed with computers, tablets and mobile devices. Just use EASE whenever you want.' } },
    ] as { title: Pair; desc: Pair; img: string }[],
  },
  users: {
    whoTitle: { t: '誰在使用 易學平台？', e: 'Who is using EASE?' },
    stat: {
      prefix: { t: '服務超過', e: 'Over' }, n1: '120', mid: { t: '間學校、近', e: 'schools,' },
      n2: '1500', suffix: { t: '位教師和學生', e: 'teachers and students using EASE' },
    },
    desc: {
      t: '我們的用戶主要為香港、中國內地和美國的學校及教育機構。此外，我們也與政府機構及非營利團體合作，如香港創科生活基金、田家炳基金會等。',
      e: 'Our users are mainly schools and educational institutions in Hong Kong, mainland China and the USA. We also collaborate with government agencies and non-profits, such as the Innovation and Technology Fund for Better Living (FBL), the Tin Ka Ping Foundation, etc.',
    },
    fundLabel: { t: '資助機構：', e: 'Funded by:' },
    funds: ['users-fund-itf', 'users-fund-tkp'],
    reviewTitle: { t: '用戶對 易學平台 有什麼評價？', e: 'What do users say about EASE?' },
    phoneImg: 'users-phone',
    testimonials: [
      { quote: { t: '「平台界面簡潔易用，趣味性強。討論區功能實用，促進了學生間的課後互動以及問題探索。」', e: '“The platform is easy to use and highly interesting. The function of the discussion area is practical, which promotes after-class interaction and problem exploration among students”' }, author: { t: '香港港島某小學王老師', e: 'A teacher from a primary school in Hong Kong Island' } },
      { quote: { t: '「平台的課堂錄像片段，方便我課後溫習功課，加深對課堂知識的理解，溫故而知新。」', e: '“By reviewing lessons after class through classroom video clips on the platform, I can deepen my understanding of knowledge, and gain new insights.”' }, author: { t: '香港九龍某小學李同學', e: 'A student from a primary school in Kowloon' } },
      { quote: { t: '「我很喜歡對話氣泡，觀看錄像時，通過點擊可視化氣泡可以快速定位課堂重難點，方便知識回顧。」', e: '“I like the dialogue bubbles. When watching a video, clicking on the visual bubbles can quickly locate the difficult points in class and facilitate knowledge review.”' }, author: { t: '香港九龍某小學張同學', e: 'A student from a primary school in Kowloon' } },
      { quote: { t: '「易學平台對改善教師教學和學生學習具有很好的效果，教師使用平台教學後，學生成績也能看到明顯的提升。」', e: '“We found the platform was effective in improving teaching and learning. Students even had higher academic scores after their teachers used the platform.”' }, author: { t: '香港某大學教師', e: 'A teacher from a university in Hong Kong' } },
    ] as { quote: Pair; author: Pair }[],
  },
  contact: {
    title: { t: '聯絡我們', e: 'Contact Us' },
    address: { t: '香港 薄扶林 香港大學 教育學院', e: 'Faculty of Education, The University of Hong Kong, Pokfulam Road, Hong Kong' },
    email: 'v2elearn@gmail.com',
    phone: '(+852) 3917-7610',
    img: 'contact-people',
  },
};
